// A1 Screening blog — Naples-first screen-care guides.
//
// Wording rules (same cautious-language locks as the rest of the site):
// no exact pricing, no same-day/24-7/emergency promises, no guarantees or
// warranty claims, no before/after claims, no diagnosis — final
// recommendations and pricing are always confirmed after technician review.

export const BLOG_POSTS = [
    {
        slug: "pool-cage-screen-repair-vs-rescreen-naples",
        title: "Screen Repair vs. Full Rescreen: How Naples Homeowners Can Tell What Their Pool Cage Needs",
        metaTitle: "Pool Cage Repair vs. Rescreen · Naples, FL Guide · A1 Screening",
        metaDescription:
            "One torn panel or a full rescreen? A practical guide for Naples homeowners on how pool cage screen repair and rescreening differ, and what a technician checks.",
        category: "Repair Basics",
        date: "2026-06-10",
        readTime: 5,
        heroPhoto: "pool-cage-completed-work-01",
        excerpt:
            "One torn panel doesn't always mean a full rescreen — and a full rescreen isn't always overkill. Here's how to think about the difference before you request an estimate.",
        sections: [
            {
                heading: "The short version",
                paragraphs: [
                    "If one or two panels are damaged and the surrounding screens still feel firm and springy, a spot repair is often the first thing worth discussing. If many panels are brittle, sagging, or tearing on their own, a full rescreen conversation usually makes more sense.",
                    "That said, every pool cage is different. The only way to know for sure is to have a technician look at the enclosure — which is exactly what a free estimate is for.",
                ],
            },
            {
                heading: "When a spot repair usually fits",
                paragraphs: [
                    "Spot repairs — replacing individual panels — tend to fit situations like these:",
                ],
                list: [
                    "A single tear from a falling branch, a pet, or an accidental bump",
                    "One or two panels pulled loose from the spline groove",
                    "Screens that are otherwise recent and still flexible to the touch",
                    "Damage limited to one area of the cage rather than spread across it",
                ],
            },
            {
                heading: "When a full rescreen is usually worth discussing",
                paragraphs: [
                    "Screens in Southwest Florida live a hard life — intense sun, salt air, summer storms. Over time the mesh itself becomes brittle. Signs that the whole enclosure may be reaching that point include:",
                ],
                list: [
                    "Multiple panels tearing within a short period, without an obvious cause",
                    "Mesh that crumbles or cracks when you press on it gently",
                    "A chalky, faded look across large areas of the cage",
                    "Panels that keep coming loose after being re-splined",
                ],
            },
            {
                heading: "What a technician actually checks",
                paragraphs: [
                    "During an estimate visit, a technician looks at more than the torn panel: the condition of the surrounding mesh, the spline, the cage frame and fasteners, doors and hardware, and how the enclosure has weathered overall. That's why we don't quote from photos alone — photos help us prepare, but the recommendation and pricing are confirmed in person.",
                ],
            },
            {
                heading: "Next steps for Naples homeowners",
                paragraphs: [
                    "If you're weighing repair against rescreen, request a free estimate and let a technician walk the cage with you. There's no obligation, and you'll get an honest read on whether a small fix will hold or whether the mesh is at the end of its run.",
                ],
            },
        ],
        faq: [
            {
                q: "Can you tell me from a photo whether I need a repair or a rescreen?",
                a: "Photos are a helpful head start — you can share them on our Upload Photos page — but the final recommendation is confirmed after a technician reviews the enclosure in person.",
            },
            {
                q: "Is it worth repairing just one panel?",
                a: "Often, yes — if the surrounding screens are still in good shape. A technician will tell you honestly if a single-panel repair makes sense or if the mesh around it is too far gone.",
            },
        ],
        related: [
            "how-long-do-pool-cage-screens-last-in-southwest-florida",
            "what-affects-pool-cage-rescreening-cost-naples",
        ],
    },
    {
        slug: "how-long-do-pool-cage-screens-last-in-southwest-florida",
        title: "How Long Do Pool Cage Screens Last in Southwest Florida?",
        metaTitle: "How Long Do Pool Cage Screens Last? · SWFL Guide · A1 Screening",
        metaDescription:
            "Sun, salt air, and storms all shorten screen life in Naples and Southwest Florida. What wears screens out, the signs to watch for, and when to plan ahead.",
        category: "Maintenance",
        date: "2026-06-10",
        readTime: 4,
        heroPhoto: "pool-cage-completed-work-02",
        excerpt:
            "Screen life in Naples isn't like screen life anywhere else. Here's what actually wears pool cage screens out in Southwest Florida — and the early signs they're ageing.",
        sections: [
            {
                heading: "There's no single number — here's why",
                paragraphs: [
                    "Ask five contractors how long screens last and you'll hear five answers, because it genuinely depends. In Southwest Florida, many enclosures show meaningful wear somewhere in the range of several years to well over a decade — the spread is that wide because sun exposure, mesh quality, storm history, and maintenance all differ from home to home.",
                    "What we can say with confidence: screens in Naples age faster than screens in most of the country, and the reasons are predictable.",
                ],
            },
            {
                heading: "What wears screens out here",
                list: [
                    "UV exposure — the Florida sun slowly makes mesh brittle, especially on south- and west-facing panels",
                    "Salt air — coastal humidity is hard on both mesh and the fasteners holding the cage together",
                    "Storms — wind-driven debris causes tears, and repeated flexing loosens spline over time",
                    "Vegetation — branches rubbing on panels wear through mesh surprisingly quickly",
                    "Pets and traffic — doors and lower panels take the most day-to-day contact",
                ],
            },
            {
                heading: "Early signs your screens are ageing",
                paragraphs: [
                    "You don't need a ladder to spot most of these. From the pool deck, look for:",
                ],
                list: [
                    "Fading or a chalky gray tone spreading across panels",
                    "Mesh that feels stiff or crackly instead of springy",
                    "Small tears appearing without an obvious cause",
                    "Panels bowing or pulling away at the edges",
                    "Screws and fasteners showing rust streaks",
                ],
            },
            {
                heading: "Planning ahead beats reacting",
                paragraphs: [
                    "If your screens are showing several of the signs above, it's worth getting an estimate before storm season rather than after — you'll have more scheduling flexibility, and you avoid living with open panels while you wait. Estimates are free, and a technician will give you a straight answer about how much life your current screens have left.",
                ],
            },
        ],
        faq: [
            {
                q: "Do some screen meshes last longer than others?",
                a: "Mesh quality and weave do make a difference, and a technician can walk you through the options available for your enclosure during a free estimate.",
            },
            {
                q: "Should I replace all the screens at once or as they fail?",
                a: "It depends on the overall condition of the mesh. If most panels are from the same era and ageing together, panel-by-panel replacement can become a treadmill — a technician review will tell you which situation you're in.",
            },
        ],
        related: [
            "pool-cage-screen-repair-vs-rescreen-naples",
            "preparing-your-lanai-and-pool-cage-for-hurricane-season-naples",
        ],
    },
    {
        slug: "what-affects-pool-cage-rescreening-cost-naples",
        title: "What Affects the Cost of Pool Cage Rescreening in Naples?",
        metaTitle: "Pool Cage Rescreening Cost Factors · Naples, FL · A1 Screening",
        metaDescription:
            "Why there's no honest one-size price for pool cage rescreening in Naples: panel count, cage height, mesh type, frame condition, and access all matter.",
        category: "Cost & Estimates",
        date: "2026-06-10",
        readTime: 4,
        heroPhoto: "pool-cage-completed-work-03",
        excerpt:
            "You won't find a price list on our site — and there's a reason for that. These are the factors that actually drive rescreening cost, and why estimates are done in person.",
        sections: [
            {
                heading: "Why we don't publish prices",
                paragraphs: [
                    "Two pool cages that look similar from the street can be very different jobs. One might be a straightforward single-story enclosure with healthy framing; the other might be a two-story cage with brittle fasteners, tricky access, and doors that need rework. Publishing a single number would either mislead you or pad every job to cover the worst case — neither is fair.",
                    "Instead, estimates are free, and the price you're given comes after a technician has actually looked at your enclosure.",
                ],
            },
            {
                heading: "The factors that drive the price",
                list: [
                    "Panel count and cage size — more panels and taller walls mean more material and more time",
                    "Cage height — second-story and high-roof sections take more setup and care",
                    "Mesh type — standard mesh, tighter no-see-um weaves, and specialty options are priced differently",
                    "Frame and fastener condition — worn spline grooves or rusted fasteners can add prep work",
                    "Doors and hardware — screen doors, sweeps, and latches are their own line items",
                    "Access — landscaping, pool equipment, screened areas over water features, and tight lot lines all affect the work",
                ],
            },
            {
                heading: "Repair vs. rescreen changes the math",
                paragraphs: [
                    "If only a couple of panels are damaged and the rest of the mesh is healthy, a spot repair is usually a much smaller job than a full rescreen. If the mesh is failing across the cage, panel-by-panel repairs can end up costing more over time than doing it once. A technician will tell you which side of that line your enclosure is on.",
                ],
            },
            {
                heading: "How to get a real number",
                paragraphs: [
                    "Request a free estimate — by phone, through the Free Estimate page, or by sharing photos on the Upload Photos page so we arrive prepared. You'll get a specific price for your specific cage, not a guess.",
                ],
            },
        ],
        faq: [
            {
                q: "Can you give me a ballpark over the phone?",
                a: "We'd rather not guess — a number given blind is usually wrong in one direction or the other. Estimates are free, and the price is confirmed after technician review of your actual enclosure.",
            },
            {
                q: "Does sending photos get me a price faster?",
                a: "Photos help us prepare and understand the job, but they don't replace the in-person look. Final pricing always follows technician review.",
            },
        ],
        related: [
            "pool-cage-screen-repair-vs-rescreen-naples",
            "no-see-um-screens-vs-standard-mesh-naples",
        ],
    },
    {
        slug: "preparing-your-lanai-and-pool-cage-for-hurricane-season-naples",
        title: "Preparing Your Lanai & Pool Cage for Hurricane Season in Naples",
        metaTitle: "Hurricane Season Pool Cage Prep · Naples, FL · A1 Screening",
        metaDescription:
            "A practical pre-season checklist for Naples pool cages and lanais: what to inspect, what to secure before a storm, and what to do (safely) afterward.",
        category: "Storm Season",
        date: "2026-06-10",
        readTime: 5,
        heroPhoto: "pool-cage-completed-work-04",
        excerpt:
            "June through November is the season your pool cage earns its keep. A practical checklist for before the season, before a storm, and after one passes.",
        sections: [
            {
                heading: "Before the season: a ten-minute walkthrough",
                paragraphs: [
                    "Early summer is the right time to walk your enclosure slowly and look for weak points, while contractors' schedules are still flexible:",
                ],
                list: [
                    "Press gently on a few panels around the cage — healthy mesh springs back, brittle mesh feels stiff or crackly",
                    "Look for panels already pulling out of the spline groove, especially along the roofline",
                    "Check doors: hinges, latches, and sweeps take more abuse than any panel",
                    "Note rust streaks at fasteners and any framing that looks out of line",
                    "Trim back branches and vegetation that touch or overhang the cage",
                ],
            },
            {
                heading: "When a storm is coming",
                list: [
                    "Bring in or secure lanai furniture, planters, and anything that can become airborne",
                    "Don't attempt last-minute screen or frame work yourself, especially at height",
                    "Photograph your enclosure while it's intact — useful for insurance if damage occurs",
                ],
            },
            {
                heading: "After the storm — safety first",
                paragraphs: [
                    "Once conditions are safe, inspect from the ground only. Sagging framing, hanging panels, and debris on the cage roof are jobs for a technician with the right equipment — not a homeowner on a ladder.",
                    "Take clear photos of any damage for your records and your insurer, then request an estimate. After a major storm, requests are worked through in order — we won't promise you a specific arrival window we can't keep, and we'd encourage you to be cautious of anyone who does.",
                ],
            },
            {
                heading: "Storm-damaged screens and insurance",
                paragraphs: [
                    "Many homeowners coordinate screen enclosure repairs with an insurance claim. Your photos, dates, and a written estimate all help that process. We're happy to provide documentation of what we find during the estimate visit — your insurer and adjuster make the coverage decisions.",
                ],
            },
        ],
        faq: [
            {
                q: "Should pool cage screens be cut before a hurricane to save the frame?",
                a: "You'll hear strong opinions both ways, and the honest answer is that it depends on the storm and the enclosure. It's a conversation to have with a technician before the season — not a decision to improvise at the last minute.",
            },
            {
                q: "How fast can you come out after a storm?",
                a: "After a major storm we work through requests in order and won't promise specific arrival windows. Getting your request in early — with photos — helps us prepare for the visit.",
            },
        ],
        related: [
            "how-long-do-pool-cage-screens-last-in-southwest-florida",
            "why-are-bugs-getting-into-my-lanai-naples",
        ],
    },
    {
        slug: "why-are-bugs-getting-into-my-lanai-naples",
        title: "Why Are Bugs Getting Into My Lanai? Common Screen Problems We See in Naples",
        metaTitle: "Bugs Getting Into Your Lanai? Common Causes · Naples · A1 Screening",
        metaDescription:
            "Mosquitoes and no-see-ums inside your Naples lanai usually mean a specific screen problem. The usual suspects — and what you can safely check yourself.",
        category: "Repair Basics",
        date: "2026-06-10",
        readTime: 4,
        heroPhoto: "pool-cage-completed-work-05",
        excerpt:
            "If you're swatting mosquitoes inside your screened lanai, something specific is letting them in. Here are the usual suspects, roughly in the order we find them.",
        sections: [
            {
                heading: "The usual suspects",
                paragraphs: [
                    "Screens either keep bugs out or they don't — so when insects show up inside a screened lanai, there's almost always a findable entry point. Roughly in the order we encounter them:",
                ],
                list: [
                    "Small tears and punctures — even a coin-sized hole is a highway for mosquitoes",
                    "Worn or shrunken spline — the rubber cord holding mesh in its groove ages, letting panel edges lift",
                    "Door gaps — a worn sweep, a sagging hinge, or a latch that doesn't pull the door tight",
                    "Panels pulled loose at the frame — often high up where you won't notice from a chair",
                    "Standard mesh vs. tiny insects — no-see-ums can pass through some standard weaves even when the screen is intact",
                ],
            },
            {
                heading: "What you can safely check yourself",
                list: [
                    "Walk the cage at eye level and scan each panel against the light — tears show up as bright lines",
                    "Check every door: does it close tight on its own, and does the sweep touch the floor?",
                    "Look along panel edges for lifted spline or mesh pulling out of the groove",
                    "Do your checks from the ground — leave roofline panels to a technician",
                ],
            },
            {
                heading: "If it's no-see-ums specifically",
                paragraphs: [
                    "If your screens look intact but tiny biting midges still get through, the mesh weave itself may be the issue. Tighter no-see-um mesh exists exactly for this — see our guide comparing it with standard mesh, and a technician can confirm whether it suits your enclosure.",
                ],
            },
            {
                heading: "Getting it fixed",
                paragraphs: [
                    "Most bug-entry problems are on the smaller end of screen repairs — a panel here, a door sweep there. Request a free estimate, mention where you're seeing the bugs, and share photos if you have them. The fix is confirmed after a technician looks at the enclosure.",
                ],
            },
        ],
        faq: [
            {
                q: "Can one small tear really be the whole problem?",
                a: "Yes — mosquitoes are remarkably good at finding a single opening. That said, a technician will check the rest of the enclosure too, since spline and door wear often show up together.",
            },
            {
                q: "Do you repair screen doors as well as panels?",
                a: "Yes — screen doors, sweeps, hinges, and latches are one of our regular services in Naples and across Southwest Florida.",
            },
        ],
        related: [
            "no-see-um-screens-vs-standard-mesh-naples",
            "pool-cage-screen-repair-vs-rescreen-naples",
        ],
    },
    {
        slug: "no-see-um-screens-vs-standard-mesh-naples",
        title: "No-See-Um Screens vs. Standard Mesh: Which Is Right for Your Naples Lanai?",
        metaTitle: "No-See-Um Screen vs. Standard Mesh · Naples Lanai · A1 Screening",
        metaDescription:
            "Standard 18/14 mesh or tighter 20/20 no-see-um screen? The real trade-offs for Naples lanais — insect protection, airflow, visibility, and durability.",
        category: "Screen Options",
        date: "2026-06-10",
        readTime: 4,
        heroPhoto: "pool-cage-completed-work-01",
        excerpt:
            "Near the coast, standard screen mesh doesn't always stop the smallest biters. Here's how no-see-um mesh differs — and the trade-offs to weigh before a rescreen.",
        sections: [
            {
                heading: "The two meshes, in plain English",
                paragraphs: [
                    "Most Florida pool cages use a standard mesh often described as an 18/14 weave — the numbers refer to the strands per inch in each direction. It handles mosquitoes and larger insects well and moves air freely.",
                    "No-see-um mesh — commonly a 20/20 weave — is woven tighter specifically to block the tiny biting midges that coastal and near-water homes know too well. If you've been bitten inside an intact screen enclosure, this is usually the mesh people are asking about.",
                ],
            },
            {
                heading: "The trade-offs to weigh",
                list: [
                    "Insect protection — the tighter weave is the whole point: it blocks insects small enough to pass some standard mesh",
                    "Airflow — a tighter weave passes slightly less breeze; most people find it a minor difference, but it's real",
                    "Visibility — finer mesh can read as slightly 'denser' visually, depending on light and color",
                    "Debris and cleaning — tighter mesh can hold pollen and fine debris a bit more, so occasional rinsing helps",
                ],
            },
            {
                heading: "Where each one tends to fit in Naples",
                paragraphs: [
                    "Homes near the Gulf, canals, mangroves, and preserves are the classic no-see-um cases — that's where the tiny biters are thickest around dawn and dusk. Inland lanais with a standard mosquito problem are often well served by standard mesh.",
                    "Some homeowners mix approaches by area of the enclosure. Whether that makes sense for your cage — and which mesh suits your framing — is confirmed by a technician during a free estimate.",
                ],
            },
            {
                heading: "Thinking about it as part of a rescreen?",
                paragraphs: [
                    "Mesh choice is one of the factors that shapes a rescreening quote, so it's the perfect question to raise at estimate time. There's no pressure toward the pricier option — the right answer depends on where you live and how you use the lanai.",
                ],
            },
        ],
        faq: [
            {
                q: "Can I put no-see-um mesh on just part of my pool cage?",
                a: "It's sometimes done — for example on the sides facing a preserve. Whether it makes sense for your enclosure is something a technician can confirm during a free estimate.",
            },
            {
                q: "Does no-see-um mesh cost more than standard mesh?",
                a: "Mesh type is one of the factors in a rescreening price, and finer weaves are generally priced differently than standard mesh. Exact pricing for your cage is confirmed after technician review.",
            },
        ],
        related: [
            "why-are-bugs-getting-into-my-lanai-naples",
            "what-affects-pool-cage-rescreening-cost-naples",
        ],
    },
];

export const getPost = (slug) => BLOG_POSTS.find((p) => p.slug === slug);
